import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Grid,
  InputAdornment,
  Avatar,
  Divider,
  Tabs,
  Tab,
  TableSortLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import LockResetIcon from '@mui/icons-material/LockReset';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import KeyIcon from '@mui/icons-material/Key';
import StoreIcon from '@mui/icons-material/Store';
import BusinessIcon from '@mui/icons-material/Business';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import KeyOffIcon from '@mui/icons-material/KeyOff';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ShieldIcon from '@mui/icons-material/Shield';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { api } from '../lib/apiClient';
import {
  AdminUser,
  CreateAdminUserPayload,
  Dealer,
  Tenant,
  UpdateAdminUserPayload,
} from '../types';
import { useAuth } from '../state/AuthContext';
import { useToast } from '../state/ToastContext';
import { useTranslation } from 'react-i18next';
import { parseErrorMessage, getApiErrorMessage } from '../utils/errorUtils';
import { TablePaginationControl, usePagination } from '../components/TablePaginationControl';
import CustomButton from '../components/CustomButton';
import FormDialog from '../components/FormDialog';
import { createUserSchema, updateUserSchema, resetPasswordSchema } from '../schemas/user.schema';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { useSortableTable } from '../hooks/useSortableTable';
import { usePersistedColumns } from '../hooks/usePersistedColumns';
import { TableColumnSelector, ColumnDefinition } from '../components/TableColumnSelector';

import { ExportMenu } from '../components/ExportMenu';
import { FilterToggleProvider, FilterButton, FilterCollapse } from '../components/FilterToggle';

interface UserFormState {
  id?: string;
  username: string;
  email: string;
  displayName: string;
  password: string;
  role: 'master_full' | 'dealer_admin' | 'tenant_admin' | 'tenant_user';
  dealerId: string | null;
  tenantId: string | null;
  disabled: boolean;
  mustChangePassword: boolean;
}

type FormMode = 'create' | 'edit';

export default function UsersPage() {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const { t } = useTranslation();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [filterDealer, setFilterDealer] = useState('');
  const [filterTenant, setFilterTenant] = useState('');
  const [form, setForm] = useState<UserFormState | null>(null);
  const [resetModal, setResetModal] = useState<{
    id: string;
    username: string;
  } | null>(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [disableConfirmTarget, setDisableConfirmTarget] = useState<boolean | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<AdminUser | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<FormMode>('create');
  const [activeFormTab, setActiveFormTab] = useState(0);
  const { page, setPage, rowsPerPage, setRowsPerPage, paginateData } = usePagination(10);

  const columns: ColumnDefinition<AdminUser>[] = useMemo(
    () => [
      { id: 'username', label: t('users.table.username'), sortable: true },
      { id: 'email', label: t('users.table.email'), sortable: true },
      { id: 'role', label: t('users.table.role'), sortable: true },
      { id: 'dealerId', label: t('users.table.dealer'), sortable: true },
      { id: 'tenantId', label: t('users.table.tenant'), sortable: true },
      { id: 'disabled', label: t('users.table.status'), sortable: true },
      { id: 'actions', label: t('users.table.actions') },
    ],
    [t],
  );

  const [visibleColumns, setVisibleColumns] = usePersistedColumns(
    'users-columns',
    columns.map((c) => c.id),
  );

  const { sortedData, order, orderBy, handleRequestSort } = useSortableTable<AdminUser>(
    useMemo(
      () =>
        users
          .filter((u) => u.id !== user?.id)
          .filter((u) => (activeTab === 'active' ? !u.disabled : u.disabled)),
      [users, user?.id, activeTab],
    ),
    'username',
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isTenantAdmin = user?.role === 'tenant_admin';
  const isTenantUser = user?.role === 'tenant_user';

  // Filtra o próprio usuário logado da lista para evitar auto-edição acidental
  const displayUsers = useMemo(() => users.filter((u) => u.id !== user?.id), [users, user?.id]);

  const dealerById = useMemo(() => {
    const map = new Map<string, Dealer>();
    dealers.forEach((dealer) => {
      map.set(dealer.id, dealer);
    });
    return map;
  }, [dealers]);

  const tenantById = useMemo(() => {
    const map = new Map<string, Tenant>();
    tenants.forEach((tenant) => {
      map.set(tenant.id, tenant);
    });
    return map;
  }, [tenants]);

  const handleCopy = useCallback((value: string) => {
    if (!value) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).catch((error) => {
        console.warn('Failed to copy text', error);
      });
      return;
    }
    if (typeof document === 'undefined') {
      return;
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    } catch (error) {
      console.warn('Failed to copy text', error);
    }
  }, []);

  const generatePassword = useCallback(() => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*';
    let pass = '';
    for (let i = 0; i < 12; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pass;
  }, []);

  const roleCardData = useMemo(
    () => [
      {
        role: 'master_full' as UserFormState['role'],
        icon: <ShieldIcon />,
      },
      {
        role: 'dealer_admin' as UserFormState['role'],
        icon: <StoreIcon />,
      },
      {
        role: 'tenant_admin' as UserFormState['role'],
        icon: <BusinessIcon />,
      },
      {
        role: 'tenant_user' as UserFormState['role'],
        icon: <PersonIcon />,
      },
    ],
    [],
  );

  const passwordRequirements = useMemo(
    () => [
      {
        label: t('resetPassword.requirements.minLength'),
        test: (pwd: string) => pwd.length >= 8,
      },
      {
        label: t('resetPassword.requirements.uppercase'),
        test: (pwd: string) => /[A-Z]/.test(pwd),
      },
      {
        label: t('resetPassword.requirements.lowercase'),
        test: (pwd: string) => /[a-z]/.test(pwd),
      },
      {
        label: t('resetPassword.requirements.number'),
        test: (pwd: string) => /\d/.test(pwd),
      },
    ],
    [t],
  );

  const calcPasswordStrength = useCallback(
    (password: string) => {
      const passed = passwordRequirements.filter((req) => req.test(password)).length;
      return (passed / passwordRequirements.length) * 100;
    },
    [passwordRequirements],
  );

  const getStrengthColor = useCallback(
    (strength: number): 'error' | 'warning' | 'success' => {
      if (strength < 50) return 'error';
      if (strength < 100) return 'warning';
      return 'success';
    },
    [],
  );

  const getStrengthLabel = useCallback(
    (strength: number) => {
      if (strength < 50) return t('resetPassword.strength.weak');
      if (strength < 100) return t('resetPassword.strength.medium');
      return t('resetPassword.strength.strong');
    },
    [t],
  );

  const renderAssociationCell = (id?: string | null, name?: string | null) => {
    if (!id) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('common.notAvailable')}
        </Typography>
      );
    }
    const displayLabel = name ?? id;
    return (
      <Tooltip
        arrow
        placement="top"
        title={
          <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace' }}>
            ID: {id}
          </Typography>
        }
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Typography component="span" variant="body2" sx={{ cursor: 'help' }}>
            {displayLabel}
          </Typography>
          <Tooltip title={t('common.copy')}>
            <span>
              <CustomButton
                variant="icon"
                icon={<ContentCopyIcon fontSize="small" />}
                iconPosition="only"
                iconColor="primary"
                customSize={{ width: 36, height: 36 }}
                onClick={(event) => {
                  event.stopPropagation();
                  handleCopy(id);
                }}
                aria-label={t('common.copy')}
              />
            </span>
          </Tooltip>
        </Box>
      </Tooltip>
    );
  };

  const scopedDealers = useMemo(() => {
    if (!user) return [];
    if (user.role === 'dealer_admin' && user.dealerId) {
      return dealers.filter((dealer) => dealer.id === user.dealerId);
    }
    return dealers;
  }, [dealers, user]);

  const scopedTenants = useMemo(() => {
    if (!user) return [];
    if (user.role === 'dealer_admin' && user.dealerId) {
      return tenants.filter(
        (tenant) => tenant.dealerId === user.dealerId && tenant.status === 'active',
      );
    }
    if (user.role === 'tenant_admin' && user.tenantId) {
      return tenants.filter((tenant) => tenant.id === user.tenantId);
    }
    return tenants.filter((t) => t.status === 'active');
  }, [tenants, user]);

  const ROLE_ORDER: Array<UserFormState['role']> = [
    'master_full',
    'dealer_admin',
    'tenant_admin',
    'tenant_user',
  ];

  const roleOptions = useMemo(() => {
    if (!user) return ['tenant_user'] as Array<UserFormState['role']>;
    switch (user.role) {
      case 'master_full':
        return ROLE_ORDER;
      case 'dealer_admin':
        return ['dealer_admin', 'tenant_admin', 'tenant_user'];
      case 'tenant_admin':
        return ['tenant_admin', 'tenant_user'];
      default:
        return ['tenant_user'];
    }
  }, [user]);

  const defaultDealerId = user?.dealerId ?? scopedDealers[0]?.id ?? null;
  const defaultTenantId = user?.tenantId ?? scopedTenants[0]?.id ?? null;

  const roleNeedsTenant = (role?: UserFormState['role']) =>
    role === 'tenant_admin' || role === 'tenant_user';

  const defaultRole = () => {
    if (roleOptions.includes('tenant_user')) return 'tenant_user';
    return roleOptions[0] ?? 'tenant_user';
  };

  const loadReferences = async () => {
    if (!user) return;
    try {
      const promises = [];
      if (user.role === 'master_full' || user.role === 'dealer_admin') {
        promises.push(api.listDealers(null));
        promises.push(api.listTenants(null, undefined, { includeDetails: false }));
      }

      if (promises.length > 0) {
        const [dealerList, tenantList] = await Promise.all(promises);
        setDealers((dealerList as Dealer[]) || []);
        setTenants((tenantList as Tenant[]) || []);
      }
    } catch (err) {
      console.warn('Falha ao carregar dealers/tenants', err);
    }
  };

  const loadUsers = async (dealerId?: string, tenantId?: string) => {
    setLoading(true);
    try {
      const data = await api.listUsers(null, { dealerId, tenantId, includeDetails: false });
      setUsers(data);
    } catch (err) {
      showError(parseErrorMessage(err, t('users.errors.load')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReferences();
  }, [null]);

  useEffect(() => {
    const dealerScope = user?.role === 'dealer_admin' ? (user.dealerId ?? '') : filterDealer;
    const tenantScope = user?.role === 'tenant_admin' ? (user.tenantId ?? '') : filterTenant;
    loadUsers(dealerScope || undefined, tenantScope || undefined);
  }, [null, filterDealer, filterTenant, user?.role, user?.dealerId, user?.tenantId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form) return;
    setBusy(true);
    try {
      if (mode === 'create') {
        if (!form.username.trim() || !form.email.trim() || !form.password.trim()) {
          showError(t('users.errors.missingRequired'));
          return;
        }
        const payload: CreateAdminUserPayload = {
          username: form.username.trim(),
          email: form.email.trim(),
          displayName: form.displayName.trim() || form.username.trim(),
          password: form.password.trim(),
          role: form.role,
          dealerId: isTenantAdmin ? (user?.dealerId ?? null) : form.dealerId,
          tenantId: isTenantAdmin ? (user?.tenantId ?? null) : form.tenantId,
          mustChangePassword: true,
        };
        await api.createUser(null, payload);
        showSuccess(t('users.messages.created'));
      } else if (mode === 'edit' && form.id) {
        if (!form.email.trim()) {
          showError(t('users.errors.missingEmail'));
          return;
        }
        const payload: UpdateAdminUserPayload = {
          email: form.email.trim(),
          displayName: form.displayName.trim() || form.username,
          role: form.role,
          dealerId: isTenantAdmin ? (user?.dealerId ?? null) : form.dealerId,
          tenantId: isTenantAdmin ? (user?.tenantId ?? null) : form.tenantId,
          disabled: form.disabled,
        };
        await api.updateUser(null, form.id, payload);
        showSuccess(t('users.messages.updated'));
      }
      setForm(null);
      const dealerScope = user?.role === 'dealer_admin' ? (user.dealerId ?? '') : filterDealer;
      const tenantScope = user?.role === 'tenant_admin' ? (user.tenantId ?? '') : filterTenant;
      await loadUsers(dealerScope || undefined, tenantScope || undefined);
    } catch (err: any) {
      if (err.code === 'TENANT_INACTIVE') {
        showError(t('users.errors.tenantInactive'));
      } else {
        showError(parseErrorMessage(err, t('users.errors.save')));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!resetModal) return;
    const newPassword = resetPasswordValue.trim();
    if (!newPassword) {
      showError(t('users.errors.missingPassword'));
      return;
    }
    setBusy(true);
    try {
      await api.resetUserPassword(null, resetModal.id, newPassword);
      showSuccess(t('users.messages.passwordReset'));
      setResetModal(null);
    } catch (err) {
      showError(parseErrorMessage(err, t('users.errors.reset')));
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.deleteUser(null, deleteTarget.id);
      showSuccess(t('users.messages.disabled', 'Usuário desativado com sucesso.'));
      // Reload
      const dealerScope = user?.role === 'dealer_admin' ? (user.dealerId ?? '') : filterDealer;
      const tenantScope = user?.role === 'tenant_admin' ? (user.tenantId ?? '') : filterTenant;
      await loadUsers(dealerScope || undefined, tenantScope || undefined);
    } catch (err) {
      showError(parseErrorMessage(err, t('users.errors.delete')));
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  const handleDeleteClick = (adminUser: AdminUser) => {
    setDeleteTarget(adminUser);
  };

  const handleRestoreClick = (adminUser: AdminUser) => {
    setRestoreTarget(adminUser);
  };

  const handleConfirmDisable = () => {
    if (!form) return;
    setForm((prev) => (prev ? { ...prev, disabled: true } : prev));
    setDisableConfirmTarget(null);
  };

  const handleConfirmRestore = async () => {
    if (!restoreTarget) return;
    setBusy(true);
    try {
      await api.updateUser(null, restoreTarget.id, {
        email: restoreTarget.email,
        displayName: restoreTarget.displayName,
        disabled: false,
      });
      showSuccess(t('users.messages.restored', 'Usuário reativado com sucesso.'));
      // Reload
      const dealerScope = user?.role === 'dealer_admin' ? (user.dealerId ?? '') : filterDealer;
      const tenantScope = user?.role === 'tenant_admin' ? (user.tenantId ?? '') : filterTenant;
      await loadUsers(dealerScope || undefined, tenantScope || undefined);
    } catch (err: any) {
      if (err.code === 'TENANT_INACTIVE') {
        showError(t('users.errors.tenantInactive'));
      } else {
        showError(getApiErrorMessage(err, t, 'users.errors.restore'));
      }
    } finally {
      setBusy(false);
      setRestoreTarget(null);
    }
  };

  return (
    <Stack spacing={3}>
      <FilterToggleProvider>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="flex-end"
          alignItems="center"
          spacing={2}
        >
          <Stack direction="row" spacing={1}>
            <ExportMenu
              data={sortedData}
              filename="users-export"
              columns={[
                { id: 'username', label: t('users.table.username') },
                { id: 'email', label: t('users.table.email') },
                {
                  id: 'role',
                  label: t('users.table.role'),
                  transform: (val) => t(`roles.${val}`),
                },
                {
                  id: 'dealerId',
                  label: t('users.table.dealer'),
                  transform: (id) => dealerById.get(id)?.name || id || '—',
                },
                {
                  id: 'tenantId',
                  label: t('users.table.tenant'),
                  transform: (id) => tenantById.get(id)?.name || id || '—',
                },
                {
                  id: 'disabled',
                  label: t('users.table.status'),
                  transform: (val, row) => {
                    if (row.tenantId) {
                      const tenant = tenantById.get(row.tenantId);
                      if (tenant && tenant.status !== 'active') {
                        return t('users.status.tenantInactive', 'Tenant Inativo');
                      }
                    }
                    return val ? t('users.status.disabled') : t('users.status.active');
                  },
                },
              ]}
            />
            <FilterButton />
            {!isTenantUser && (
              <CustomButton
                variant="primary"
                onClick={() => {
                  setMode('create');
                  const initialRole = defaultRole() as UserFormState['role'];
                  setForm({
                    username: '',
                    email: '',
                    displayName: '',
                    password: '',
                    role: initialRole,
                    dealerId: defaultDealerId,
                    tenantId: roleNeedsTenant(initialRole) ? defaultTenantId : null,
                    disabled: false,
                    mustChangePassword: true,
                  });
                }}
              >
                {t('users.new')}
              </CustomButton>
            )}
            <Tooltip title={t('common.refresh')}>
              <span>
                <CustomButton
                  variant="icon"
                  icon={<RefreshIcon />}
                  iconPosition="only"
                  customSize={{ width: 36, height: 36 }}
                  onClick={() => {
                    const dealerScope =
                      user?.role === 'dealer_admin' ? (user.dealerId ?? '') : filterDealer;
                    const tenantScope =
                      user?.role === 'tenant_admin' ? (user.tenantId ?? '') : filterTenant;
                    loadUsers(dealerScope || undefined, tenantScope || undefined);
                  }}
                  disabled={loading}
                />
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <FilterCollapse>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl
              size="small"
              sx={{ minWidth: 160 }}
              disabled={user?.role === 'dealer_admin'}
            >
              <InputLabel id="filter-dealer">{t('users.filters.dealer')}</InputLabel>
              <Select
                labelId="filter-dealer"
                label={t('users.filters.dealer')}
                value={user?.role === 'dealer_admin' ? (user.dealerId ?? '') : filterDealer}
                onChange={(event) => setFilterDealer(event.target.value)}
              >
                <MenuItem value="">
                  <em>{t('common.all')}</em>
                </MenuItem>
                {dealers.map((dealer) => (
                  <MenuItem key={dealer.id} value={dealer.id}>
                    {dealer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 160 }}
              disabled={user?.role === 'tenant_admin' || isTenantUser}
            >
              <InputLabel id="filter-tenant">{t('users.filters.tenant')}</InputLabel>
              <Select
                labelId="filter-tenant"
                label={t('users.filters.tenant')}
                value={user?.role === 'tenant_admin' ? (user.tenantId ?? '') : filterTenant}
                onChange={(event) => setFilterTenant(event.target.value)}
              >
                <MenuItem value="">
                  <em>{t('common.all')}</em>
                </MenuItem>
                {scopedTenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {t('users.count', { count: displayUsers.length })}
            </Typography>
          </Stack>
        </FilterCollapse>
      </FilterToggleProvider>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => {
              setActiveTab(newValue);
              setPage(1);
            }}
            sx={{ px: 2 }}
          >
            <Tab
              value="active"
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{t('users.tabs.active', 'Ativos')}</span>
                  <Chip
                    label={displayUsers.filter((u) => !u.disabled).length}
                    size="small"
                    color="success"
                  />
                </Stack>
              }
            />
            <Tab
              value="archived"
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{t('users.tabs.archived', 'Desativados')}</span>
                  <Chip
                    label={displayUsers.filter((u) => u.disabled).length}
                    size="small"
                    color="default"
                  />
                </Stack>
              }
            />
          </Tabs>
        </Box>
        <CardContent>
          {(() => {
            // sortedData handles filtering and sorting

            if (loading) {
              return (
                <Typography variant="body2" color="text.secondary">
                  {t('users.loading')}
                </Typography>
              );
            }

            if (sortedData.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  {activeTab === 'active'
                    ? t('users.empty')
                    : t('users.emptyArchived', 'Nenhum usuário desativado.')}
                </Typography>
              );
            }

            return (
              <>
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                  <TableColumnSelector
                    columns={columns}
                    visibleColumns={visibleColumns}
                    onChange={setVisibleColumns}
                  />
                </Stack>
                <TableContainer component={Box} sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {visibleColumns.includes('username') && (
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'username'}
                              direction={orderBy === 'username' ? order : 'asc'}
                              onClick={() => handleRequestSort('username')}
                            >
                              {t('users.table.username')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('email') && (
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <TableSortLabel
                              active={orderBy === 'email'}
                              direction={orderBy === 'email' ? order : 'asc'}
                              onClick={() => handleRequestSort('email')}
                            >
                              {t('users.table.email')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('role') && (
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <TableSortLabel
                              active={orderBy === 'role'}
                              direction={orderBy === 'role' ? order : 'asc'}
                              onClick={() => handleRequestSort('role')}
                            >
                              {t('users.table.role')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('dealerId') && (
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                            <TableSortLabel
                              active={orderBy === 'dealerId'}
                              direction={orderBy === 'dealerId' ? order : 'asc'}
                              onClick={() => handleRequestSort('dealerId')}
                            >
                              {t('users.table.dealer')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('tenantId') && (
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                            <TableSortLabel
                              active={orderBy === 'tenantId'}
                              direction={orderBy === 'tenantId' ? order : 'asc'}
                              onClick={() => handleRequestSort('tenantId')}
                            >
                              {t('users.table.tenant')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('disabled') && (
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'disabled'}
                              direction={orderBy === 'disabled' ? order : 'asc'}
                              onClick={() => handleRequestSort('disabled')}
                            >
                              {t('users.table.status')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('actions') && (
                          <TableCell align="right">{t('users.table.actions')}</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginateData(sortedData).map((adminUser) => (
                        <TableRow key={adminUser.id} hover>
                          {visibleColumns.includes('username') && (
                            <TableCell>{adminUser.username}</TableCell>
                          )}
                          {visibleColumns.includes('email') && (
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                              {adminUser.email}
                            </TableCell>
                          )}
                          {visibleColumns.includes('role') && (
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                              {t(`roles.${adminUser.role}`)}
                            </TableCell>
                          )}
                          {visibleColumns.includes('dealerId') && (
                            <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                              {renderAssociationCell(
                                adminUser.dealerId,
                                adminUser.dealerId
                                  ? (dealerById.get(adminUser.dealerId)?.name ?? null)
                                  : null,
                              )}
                            </TableCell>
                          )}
                          {visibleColumns.includes('tenantId') && (
                            <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                              {renderAssociationCell(
                                adminUser.tenantId,
                                adminUser.tenantId
                                  ? (tenantById.get(adminUser.tenantId)?.name ?? null)
                                  : null,
                              )}
                            </TableCell>
                          )}
                          {visibleColumns.includes('disabled') && (
                            <TableCell>
                              {(() => {
                                const tenant = adminUser.tenantId
                                  ? tenantById.get(adminUser.tenantId)
                                  : null;
                                const isTenantInactive = tenant && tenant.status !== 'active';

                                if (isTenantInactive) {
                                  return (
                                    <Chip
                                      size="small"
                                      label={t('users.status.tenantInactive', 'Tenant Inactive')}
                                      color="error"
                                      variant="outlined"
                                    />
                                  );
                                }

                                return (
                                  <Chip
                                    size="small"
                                    label={
                                      adminUser.disabled
                                        ? t('users.status.disabled')
                                        : t('users.status.active')
                                    }
                                    color={adminUser.disabled ? 'default' : 'success'}
                                  />
                                );
                              })()}
                            </TableCell>
                          )}
                          {visibleColumns.includes('actions') && (
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                {activeTab === 'active' ? (
                                  <>
                                    <Tooltip
                                      title={
                                        isTenantUser ? t('common.view') : t('users.actions.edit')
                                      }
                                    >
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => {
                                          setMode('edit');
                                          const resolvedRole = (
                                            roleOptions.includes(adminUser.role)
                                              ? adminUser.role
                                              : defaultRole()
                                          ) as UserFormState['role'];
                                          setForm({
                                            id: adminUser.id,
                                            username: adminUser.username,
                                            email: adminUser.email,
                                            displayName: adminUser.displayName,
                                            password: '',
                                            role: resolvedRole,
                                            dealerId: adminUser.dealerId ?? defaultDealerId,
                                            tenantId:
                                              adminUser.tenantId ??
                                              (roleNeedsTenant(resolvedRole)
                                                ? defaultTenantId
                                                : null),
                                            disabled: adminUser.disabled,
                                            mustChangePassword: true,
                                          });
                                        }}
                                      >
                                        {isTenantUser ? (
                                          <VisibilityIcon fontSize="small" />
                                        ) : (
                                          <EditIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                    {!isTenantUser && (
                                      <>
                                        <Tooltip title={t('users.actions.resetPassword')}>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => {
                                              setResetModal({
                                                id: adminUser.id,
                                                username: adminUser.username,
                                              });
                                            }}
                                          >
                                            <LockResetIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                          title={t('users.actions.disable', 'Desativar Usuário')}
                                        >
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDeleteClick(adminUser)}
                                          >
                                            <BlockIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    )}
                                  </>
                                ) : (
                                  !isTenantUser && (
                                    <Tooltip title={t('users.actions.restore', 'Reativar Usuário')}>
                                      <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() => handleRestoreClick(adminUser)}
                                      >
                                        <RestoreIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )
                                )}
                              </Stack>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePaginationControl
                  count={sortedData.length}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={setPage}
                  onRowsPerPageChange={setRowsPerPage}
                />
              </>
            );
          })()}
        </CardContent>
      </Card>

      <FormDialog
        open={Boolean(form)}
        onClose={() => {
          setForm(null);
          setMode('create');
          setActiveFormTab(0);
        }}
        maxWidth="md"
        loading={busy}
        disableBackdropClick
        title={mode === 'create' ? t('users.dialog.createTitle') : t('users.dialog.editTitle')}
        subtitle={mode === 'create' ? t('users.dialog.createSubtitle') : t('users.dialog.editSubtitle')}
        icon={mode === 'create' ? <PersonAddIcon /> : <EditIcon />}
        formId="user-form"
        submitText={mode === 'create' ? t('users.dialog.submitCreate') : t('users.dialog.submitEdit')}
        disableSubmit={
          mode === 'create'
            ? !createUserSchema.safeParse(form).success
            : !updateUserSchema.safeParse(form).success
        }
        headerContent={
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
            <Tabs
              value={activeFormTab}
              onChange={(_, newValue) => setActiveFormTab(newValue)}
              aria-label={t('users.a11y.formTabs')}
            >
              <Tab label={t('users.form.tabGeneral')} id="user-tab-0" aria-controls="user-tabpanel-0" />
              <Tab label={t('users.form.tabAccess')} id="user-tab-1" aria-controls="user-tabpanel-1" />
              {mode === 'edit' && form?.id !== user?.id && (
                <Tab
                  label={t('users.form.tabStatus')}
                  id="user-tab-2"
                  aria-controls="user-tabpanel-2"
                  sx={{ color: 'error.main', '&.Mui-selected': { color: 'error.dark' } }}
                />
              )}
            </Tabs>
          </Box>
        }
      >
          <Box component="form" id="user-form" onSubmit={handleSubmit}>
            {/* TAB 0: Account Information */}
            <Box role="tabpanel" hidden={activeFormTab !== 0} id="user-tabpanel-0" aria-labelledby="user-tab-0">
              {activeFormTab === 0 && (
                <Box sx={{ pt: 1 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      letterSpacing: 0.5,
                    }}
                  >
                    {t('users.form.accountInfo')}
                  </Typography>
                  <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('users.form.username')}
                    value={form?.username ?? ''}
                    onChange={(event) =>
                      setForm((prev) => (prev ? { ...prev, username: event.target.value } : prev))
                    }
                    required
                    disabled={busy || mode === 'edit'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('users.form.email')}
                    type="email"
                    value={form?.email ?? ''}
                    onChange={(event) =>
                      setForm((prev) => (prev ? { ...prev, email: event.target.value } : prev))
                    }
                    required
                    disabled={busy}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('users.form.displayName')}
                    value={form?.displayName ?? ''}
                    onChange={(event) =>
                      setForm((prev) =>
                        prev ? { ...prev, displayName: event.target.value } : prev,
                      )
                    }
                    disabled={busy}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                {mode === 'create' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('users.form.password')}
                      type="password"
                      value={form?.password ?? ''}
                      onChange={(event) =>
                        setForm((prev) => (prev ? { ...prev, password: event.target.value } : prev))
                      }
                      required
                      disabled={busy}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <KeyIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={t('users.form.generatePassword')}>
                              <button
                                type="button"
                                onClick={() => {
                                  const pass = generatePassword();
                                  setForm((prev) => (prev ? { ...prev, password: pass } : prev));
                                }}
                                aria-label={t('users.form.generatePassword')}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 36,
                                  height: 36,
                                  border: 'none',
                                  background: 'none',
                                  cursor: 'pointer',
                                  padding: 0,
                                  color: 'inherit',
                                }}
                              >
                                <ShuffleIcon fontSize="small" />
                              </button>
                            </Tooltip>
                            <Tooltip title={t('common.copy')}>
                              <button
                                type="button"
                                onClick={() => handleCopy(form?.password ?? '')}
                                aria-label={t('common.copy')}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 36,
                                  height: 36,
                                  border: 'none',
                                  background: 'none',
                                  cursor: 'pointer',
                                  padding: 0,
                                  color: 'inherit',
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </button>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {form?.password && (
                      <Box sx={{ mt: 1.5 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            {t('resetPassword.strength.label')}
                          </Typography>
                          <Typography variant="caption" fontWeight={600} color={`${getStrengthColor(calcPasswordStrength(form.password))}.main`}>
                            {getStrengthLabel(calcPasswordStrength(form.password))}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={calcPasswordStrength(form.password)}
                          color={getStrengthColor(calcPasswordStrength(form.password))}
                          sx={{ borderRadius: 1, height: 4, mb: 1 }}
                        />
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: (theme) =>
                              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                          }}
                        >
                          <Stack spacing={0.5}>
                            {passwordRequirements.map((req, index) => {
                              const passed = req.test(form.password);
                              return (
                                <Stack key={index} direction="row" alignItems="center" spacing={0.75}>
                                  {passed ? (
                                    <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                  ) : (
                                    <ErrorOutlineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                  )}
                                  <Typography
                                    variant="caption"
                                    color={passed ? 'success.main' : 'text.secondary'}
                                    fontSize="0.75rem"
                                  >
                                    {req.label}
                                  </Typography>
                                </Stack>
                              );
                            })}
                          </Stack>
                        </Box>
                      </Box>
                    )}
                  </Grid>
                )}
              </Grid>
                </Box>
              )}
            </Box>

            {/* TAB 1: Access Control Section */}
            <Box role="tabpanel" hidden={activeFormTab !== 1} id="user-tabpanel-1" aria-labelledby="user-tab-1">
              {activeFormTab === 1 && (
                <Box sx={{ pt: 1 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      letterSpacing: 0.5,
                    }}
                  >
                    {t('users.form.accessControl')}
                  </Typography>
                  <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1.5, fontSize: '0.8125rem', fontWeight: 600 }}
                  >
                    {t('users.form.role')}
                  </Typography>
                  <Box
                    role="radiogroup"
                    aria-label={t('users.form.role')}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 1.5,
                    }}
                  >
                    {roleCardData
                      .filter((card) => roleOptions.includes(card.role))
                      .map((card) => {
                        const isSelected = form?.role === card.role;
                        return (
                          <Box
                            key={card.role}
                            role="radio"
                            aria-checked={isSelected}
                            tabIndex={0}
                            onClick={() => {
                              if (busy) return;
                              setForm((prev) => {
                                if (!prev) return prev;
                                return {
                                  ...prev,
                                  role: card.role,
                                  tenantId: roleNeedsTenant(card.role)
                                    ? (prev.tenantId ?? defaultTenantId)
                                    : null,
                                };
                              });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (busy) return;
                                setForm((prev) => {
                                  if (!prev) return prev;
                                  return {
                                    ...prev,
                                    role: card.role,
                                    tenantId: roleNeedsTenant(card.role)
                                      ? (prev.tenantId ?? defaultTenantId)
                                      : null,
                                  };
                                });
                              }
                            }}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '2px solid',
                              borderColor: isSelected ? 'primary.main' : 'divider',
                              bgcolor: isSelected
                                ? (theme) => alpha(theme.palette.primary.main, 0.06)
                                : 'background.paper',
                              cursor: busy ? 'not-allowed' : 'pointer',
                              transition: 'all 0.15s ease',
                              opacity: busy ? 0.6 : 1,
                              '&:hover': busy
                                ? {}
                                : {
                                    borderColor: isSelected ? 'primary.main' : 'text.secondary',
                                    bgcolor: isSelected
                                      ? (theme) => alpha(theme.palette.primary.main, 0.06)
                                      : (theme) => alpha(theme.palette.action.hover, 0.04),
                                  },
                            }}
                          >
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: '50%',
                                  bgcolor: isSelected
                                    ? (theme) => alpha(theme.palette.primary.main, 0.12)
                                    : (theme) => alpha(theme.palette.action.hover, 0.08),
                                  color: isSelected ? 'primary.main' : 'text.secondary',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                {card.icon}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  color={isSelected ? 'primary.main' : 'text.primary'}
                                >
                                  {t(`roles.${card.role}`)}
                                </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                  {t(`${card.role}_desc`)}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        );
                      })}
                  </Box>
                </Grid>

                {!isTenantAdmin && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={busy || user?.role === 'dealer_admin'}>
                      <InputLabel id="user-dealer-label">{t('users.form.dealer')}</InputLabel>
                      <Select
                        labelId="user-dealer-label"
                        label={t('users.form.dealer')}
                        value={form?.dealerId ?? ''}
                        onChange={(event) =>
                          setForm((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  dealerId: event.target.value || null,
                                }
                              : prev,
                          )
                        }
                        startAdornment={
                          <InputAdornment position="start">
                            <StoreIcon
                              fontSize="small"
                              sx={{ color: 'text.secondary', ml: 1, mr: -0.5 }}
                            />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value="">
                          <em>{t('common.none')}</em>
                        </MenuItem>
                        {scopedDealers.map((dealer) => (
                          <MenuItem key={dealer.id} value={dealer.id}>
                            {dealer.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {!isTenantAdmin && roleNeedsTenant(form?.role) && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={busy}>
                      <InputLabel id="user-tenant-label">{t('users.form.tenant')}</InputLabel>
                      <Select
                        labelId="user-tenant-label"
                        label={t('users.form.tenant')}
                        value={form?.tenantId ?? ''}
                        onChange={(event) =>
                          setForm((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  tenantId: event.target.value || null,
                                }
                              : prev,
                          )
                        }
                        startAdornment={
                          <InputAdornment position="start">
                            <BusinessIcon
                              fontSize="small"
                              sx={{ color: 'text.secondary', ml: 1, mr: -0.5 }}
                            />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value="">
                          <em>{t('common.none')}</em>
                        </MenuItem>
                        {scopedTenants.map((tenant) => (
                          <MenuItem key={tenant.id} value={tenant.id}>
                            {tenant.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
                </Box>
              )}
            </Box>

            {/* TAB 2: Status Section */}
            {mode === 'edit' && form?.id !== user?.id && (
              <Box role="tabpanel" hidden={activeFormTab !== 2} id="user-tabpanel-2" aria-labelledby="user-tab-2">
                {activeFormTab === 2 && (
                  <Box sx={{ pt: 1 }}>
                    <Typography
                      variant="subtitle2"
                      color="error.main"
                      sx={{
                        mb: 2,
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        letterSpacing: 0.5,
                      }}
                    >
                      {t('users.form.tabStatus')}
                    </Typography>

                    <Card
                      variant="outlined"
                      sx={{
                        borderColor: form?.disabled ? 'error.main' : 'divider',
                        bgcolor: form?.disabled
                          ? (theme) => alpha(theme.palette.error.main, 0.04)
                          : 'background.paper',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                        <Stack direction="row" alignItems="center" spacing={2.5}>
                          <Avatar
                            sx={{
                              bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
                              color: 'error.main',
                              width: 44,
                              height: 44,
                            }}
                          >
                            <BlockIcon />
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight={600} color={form?.disabled ? 'error.main' : 'text.primary'}>
                              {t('users.form.disableUser')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {t('users.form.disableUserHelper')}
                            </Typography>
                          </Box>
                          <Switch
                            checked={form?.disabled ?? false}
                            onChange={(event) => {
                              const newValue = event.target.checked;
                              if (newValue && !form?.disabled) {
                                setDisableConfirmTarget(true);
                              } else {
                                setForm((prev) =>
                                  prev ? { ...prev, disabled: newValue } : prev,
                                );
                              }
                            }}
                            disabled={busy}
                            color="error"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: 'error.main',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: 'error.main',
                              },
                            }}
                          />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </Box>
            )}
          </Box>
      </FormDialog>

      {/* Reset Password Dialog */}
      <FormDialog
        open={Boolean(resetModal)}
        onClose={() => {
          setResetModal(null);
          setResetPasswordValue('');
        }}
        title={t('users.resetDialog.title')}
        submitText={t('common.save')}
        cancelText={t('common.cancel')}
        formId="reset-form"
        loading={busy}
        disableSubmit={!resetPasswordSchema.safeParse({ password: resetPasswordValue }).success}
        maxWidth="xs"
        icon={<KeyOffIcon color="warning" />}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }} id="reset-dialog-description">
          {t('users.resetDialog.description', {
            username: resetModal?.username ?? '',
          })}
        </Typography>
        <Stack spacing={2} component="form" id="reset-form" onSubmit={handleReset}>
          <TextField
            label={t('users.resetDialog.password')}
            name="newPassword"
            type="text"
            value={resetPasswordValue}
            onChange={(e) => setResetPasswordValue(e.target.value)}
            required
            fullWidth
            disabled={busy}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={t('users.form.generatePassword')}>
                    <button
                      type="button"
                      onClick={() => {
                        const pass = generatePassword();
                        setResetPasswordValue(pass);
                      }}
                      aria-label={t('users.form.generatePassword')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        color: 'inherit',
                      }}
                    >
                      <ShuffleIcon fontSize="small" />
                    </button>
                  </Tooltip>
                  <Tooltip title={t('common.copy')}>
                    <button
                      type="button"
                      onClick={() => handleCopy(resetPasswordValue)}
                      aria-label={t('common.copy')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        color: 'inherit',
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </button>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          {resetPasswordValue && (
            <Box sx={{ textAlign: 'left' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {t('resetPassword.strength.label')}
                </Typography>
                <Typography variant="caption" fontWeight={600} color={`${getStrengthColor(calcPasswordStrength(resetPasswordValue))}.main`}>
                  {getStrengthLabel(calcPasswordStrength(resetPasswordValue))}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={calcPasswordStrength(resetPasswordValue)}
                color={getStrengthColor(calcPasswordStrength(resetPasswordValue))}
                sx={{ borderRadius: 1, height: 4, mb: 1 }}
              />
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                }}
              >
                <Stack spacing={0.5}>
                  {passwordRequirements.map((req, index) => {
                    const passed = req.test(resetPasswordValue);
                    return (
                      <Stack key={index} direction="row" alignItems="center" spacing={0.75}>
                        {passed ? (
                          <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                        ) : (
                          <ErrorOutlineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        )}
                        <Typography
                          variant="caption"
                          color={passed ? 'success.main' : 'text.secondary'}
                          fontSize="0.75rem"
                        >
                          {req.label}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            </Box>
          )}
        </Stack>
      </FormDialog>

      {/* Disable Confirmation Dialog */}
      <ConfirmationDialog
        open={Boolean(disableConfirmTarget)}
        title={t('users.form.disableUser')}
        content={t('users.confirmDelete', { name: form?.username ?? '' })}
        onClose={() => setDisableConfirmTarget(null)}
        onConfirm={handleConfirmDisable}
        severity="error"
        confirmText={t('users.actions.disable')}
        loading={busy}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={Boolean(deleteTarget)}
        title={t('common.confirmDelete', 'Desativar usuário')}
        content={t('users.confirmDelete', { name: deleteTarget?.username })}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        severity="error"
        confirmText={t('users.actions.disable', 'Desativar')}
        loading={busy}
      />

      <ConfirmationDialog
        open={Boolean(restoreTarget)}
        title={t('users.actions.restore', 'Reativar Usuário')}
        content={t('users.confirmRestore', 'Deseja reativar o usuário {{name}}?', {
          name: restoreTarget?.username,
        })}
        onClose={() => setRestoreTarget(null)}
        onConfirm={handleConfirmRestore}
        severity="info"
        confirmText={t('common.confirm', 'Confirmar')}
        loading={busy}
      />
    </Stack>
  );
}
